// ABOUTME: MFA challenge screen shown during login when user has MFA enabled.
// ABOUTME: Supports TOTP code entry and recovery code fallback.
import { Box, InputBase, Typography, useTheme } from "@mui/material";
import OtpInput from "../../components/theme/inputs/OtpInput";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Shbutton from "../../components/theme/buttons/Shbutton";
import { tokens } from "../../theme";
import { useVerifyMfaMutation, useUseRecoveryCodeMutation } from "../../newapi/user/mfaSlice";
import { motion } from "framer-motion";

const MfaChallenge = ({ mfaToken, onBack, brand, isOnline }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const [mode, setMode] = useState("totp"); // "totp" or "recovery"
    const [code, setCode] = useState("");
    const [recoveryCode, setRecoveryCode] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const [verifyMfa, { isLoading: isVerifying }] = useVerifyMfaMutation();
    const [submitRecoveryCode, { isLoading: isRecovering }] = useUseRecoveryCodeMutation();

    const handleVerifyTotp = async (codeOverride) => {
        setErrorMessage("");
        try {
            const result = await verifyMfa({ mfaToken, code: codeOverride || code }).unwrap();
            if (!result.success) {
                setErrorMessage(result.error || t("invalid_code_try_again"));
            }
        } catch (err) {
            setErrorMessage(err?.data?.error || t("invalid_code_try_again"));
        }
    };

    const handleVerifyRecovery = async () => {
        setErrorMessage("");
        try {
            const result = await submitRecoveryCode({ mfaToken, recoveryCode }).unwrap();
            if (!result.success) {
                setErrorMessage(result.error || t("invalid_code_try_again"));
            }
        } catch (err) {
            setErrorMessage(err?.data?.error || t("invalid_code_try_again"));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            if (mode === "totp" && code.length === 6) {
                handleVerifyTotp();
            } else if (mode === "recovery" && recoveryCode.length > 0) {
                handleVerifyRecovery();
            }
        }
    };

    const isLoading = isVerifying || isRecovering;

    return (
        <motion.div
            key={mode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
        >
            <Box
                sx={{
                    padding: "32px",
                    borderRadius: "24px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    paddingTop:"40px",
                    "@media screen and (max-width: 47.9375em)": {
                        p:0,
                        px:3,
                    }
                }}
            >

                <Box
                    sx={{
                        display:"flex",
                        flexDirection:"column",
                        alignItems:"center",
                        "@media screen and (max-width: 47.9375em)" : {
                            width:1,
                        }
                    }}
                >
                    <Typography variant="h1" fontWeight="1000" fontFamily="Roboto, sans-serif"
                        sx={{
                            color:colors.txt['primary'],
                            textOverflow:"ellipsis",
                            whiteSpace:"nowrap",
                            overflow:"hidden",
                            fontSize:"32px",
                            width:"450px",
                            textAlign:"center",
                            "@media screen and (max-width: 47.9375em)" : {
                                fontSize:21,
                                width:1,
                            }
                        }}
                    >
                        {mode === "totp" ? t("two_factor_authentication") : t("use_recovery_code")}
                    </Typography>
                    <Typography variant="p" fontWeight="400" fontFamily="Roboto, sans-serif"
                        sx={{
                            color:colors.txt['secondary'],
                            textOverflow:"ellipsis",
                            whiteSpace:"nowrap",
                            overflow:"hidden",
                            fontSize:"17px",
                            marginBottom:4,
                            width:"450px",
                            textAlign:"center",
                            "@media screen and (max-width: 47.9375em)" : {
                                fontSize:14,
                                marginBottom:3,
                                width:1,
                            }
                        }}
                    >
                        {mode === "totp" ? t("enter_6_digit_code") : t("enter_recovery_code")}
                    </Typography>

                    {errorMessage &&
                        <Box
                            sx={{
                                color:colors.red[400],
                                background:colors.red[100],
                                border:`1px solid ${colors.red[400]}`,
                                p:1,
                                textAlign:'center',
                                fontFamily:"Roboto,sans-serif",
                                mb:4,
                                borderRadius:5,
                                fontSize:14,
                                width:"450px"
                            }}
                        >
                            {errorMessage}
                        </Box>
                    }

                    {mode === "totp" ? (
                        <Box
                            sx={{
                                width:"450px",
                                display:"flex",
                                justifyContent:"center",
                                "@media screen and (max-width: 47.9375em)" : {
                                    width:1,
                                }
                            }}
                        >
                            <OtpInput
                                value={code}
                                onChange={(val) => { setCode(val); setErrorMessage(""); }}
                                onComplete={(val) => handleVerifyTotp(val)}
                                error={!!errorMessage}
                                autoFocus
                            />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                width:"450px",
                                borderRadius:6,
                                display:"flex",
                                alignItems:"center",
                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                border:theme.palette.mode === "dark" ? null : "1px solid rgba(255,255,255,.15)",
                                height:48,
                                pl:2,
                                "&.error": {
                                    background:colors.red[100],
                                    border: `1px solid ${colors.red[400]}`,
                                    color: colors.red[400]
                                },
                                "input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus,input:-webkit-autofill:active" : {
                                    backgroundClip:colors.txt['primary'],
                                    "webkit-text-fill-color":colors.txt['primary'],
                                    transition: "background-color 5000s ease-in-out 0s",
                                    boxShadow:`inset 0 0 20px 20px ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                    WebkitTextFillColor: colors.txt['primary'],
                                    fontSize:16
                                },
                                "@media screen and (max-width: 64em)" : {
                                    height:40,
                                    borderRadius:5,
                                    px:1,
                                },
                                "@media screen and (max-width: 47.9375em)" : {
                                    width:1,
                                }
                            }}
                            className={errorMessage ? 'error' : ''}
                        >
                            <InputBase
                                value={recoveryCode}
                                onChange={(e) => {
                                    setRecoveryCode(e.target.value);
                                    setErrorMessage("");
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder="xxxx-xxxx-xxxx"
                                autoFocus
                                sx={{
                                    pl:1.5,
                                    flex:1,
                                    color:colors.txt['primary'],
                                    fontSize:16,
                                    letterSpacing:"2px",
                                    fontFamily:"monospace",
                                }}
                            />
                        </Box>
                    )}

                    <Box
                        sx={{
                            display:"flex",
                            justifyContent:"flex-end",
                            marginTop:2,
                            width:1,
                        }}
                    >
                        <Shbutton block color={theme.palette.mode === "dark" ? "blue" : "primary"} variant="contained"
                            onClick={mode === "totp" ? handleVerifyTotp : handleVerifyRecovery}
                            disabled={mode === "totp" ? code.length !== 6 : recoveryCode.length === 0}
                            isLoading={isLoading}
                            sx={brand?.color?.button ? {
                                backgroundColor: `${theme.palette.mode === "dark" ? brand.color.button.dark : brand.color.button.light} !important`,
                                color: `${theme.palette.mode === "dark" ? brand.color.button.contrastDark : brand.color.button.contrastLight} !important`,
                                "&:hover": { backgroundColor: `${theme.palette.mode === "dark" ? brand.color.button.dark : brand.color.button.light} !important` },
                            } : {}}
                        >
                            {t("verify")}
                        </Shbutton>
                    </Box>

                    <Box
                        sx={{
                            display:"flex",
                            justifyContent:"flex-end",
                            marginTop:2,
                            width:1,
                        }}
                    >
                        <Shbutton block color={theme.palette.mode === "dark" ? "blue" : "primary"} variant="text"
                            onClick={mode === "totp"
                                ? () => { setMode("recovery"); setErrorMessage(""); setRecoveryCode(""); }
                                : () => { setMode("totp"); setErrorMessage(""); setCode(""); }
                            }
                            sx={brand?.color?.button ? {
                                color: `${theme.palette.mode === "dark" ? brand.color.button.darkText : brand.color.button.light} !important`,
                                backgroundColor: `rgba(${(theme.palette.mode === "dark" ? brand.color.button.darkTextRgb : brand.color.button.lightRgb).join(',')}, 0.08) !important`,
                                "&:hover": {
                                    backgroundColor: `rgba(${(theme.palette.mode === "dark" ? brand.color.button.darkTextRgb : brand.color.button.lightRgb).join(',')}, 0.15) !important`
                                },
                            } : {
                                backgroundColor: theme.palette.mode === "dark" ? colors.blue[80] : colors.primary[80],
                                "&:hover": {
                                    backgroundColor: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                },
                            }}
                        >
                            {mode === "totp" ? t("use_recovery_code") : t("back_to_authenticator")}
                        </Shbutton>
                    </Box>

                    <Box
                        sx={{
                            display:"flex",
                            justifyContent:"flex-end",
                            marginTop:2,
                            width:1,
                        }}
                    >
                        <Shbutton block color={theme.palette.mode === "dark" ? "primaryGreyOpposite" : "primaryGrey"} variant="text"
                            onClick={onBack}
                        >
                            {t("cancel")}
                        </Shbutton>
                    </Box>
                </Box>
            </Box>
        </motion.div>
    );
};

export default MfaChallenge;
